import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { PageHeader, Button, Card, Modal, FormField, Input, Select, formatDate } from '../ui';
import { Plus, Zap, Printer, Filter } from 'lucide-react';

const FIELDS = ['Field 1', 'Field 2', 'Field 3', 'Field 4'];
const ROUNDS = ['Pool A', 'Pool B', 'Pool C', 'Quarterfinal', 'Semifinal', 'Final', 'Championship'];
const TIME_SLOTS = ['08:00','08:30','09:00','09:15','09:30','10:00','10:30','11:00','11:30','12:00','12:30','13:00','13:30','14:00','14:30','15:00','15:30','16:00'];

function generateSchedule(teams, date, fields, slotMinutes = 60) {
  const games = [];
  const pairs = [];
  for (let i = 0; i < teams.length; i++) {
    for (let j = i + 1; j < teams.length; j++) pairs.push([teams[i], teams[j]]);
  }
  const startHour = 8;
  pairs.forEach((pair, idx) => {
    const field = fields[idx % fields.length];
    const totalMins = startHour * 60 + idx * slotMinutes;
    const h = Math.floor(totalMins / 60);
    const m = totalMins % 60;
    games.push({
      id: 'gen_' + idx,
      homeTeamId: pair[0].id,
      awayTeamId: pair[1].id,
      field,
      date,
      time: `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`,
      ageGroup: pair[0].ageGroup,
      round: 'Pool A',
      homeScore: null,
      awayScore: null,
    });
  });
  return games;
}

export default function Schedule() {
  const { games, teams, tournaments, addGame, updateGame } = useApp();
  const { isAdmin } = useAuth();
  const [filterTrn, setFilterTrn] = useState(tournaments[0]?.id || 'all');
  const [filterDate, setFilterDate] = useState('all');
  const [filterAG, setFilterAG] = useState('all');
  const [showAdd, setShowAdd] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [editScore, setEditScore] = useState(null);
  const [genForm, setGenForm] = useState({ tournamentId: tournaments[0]?.id || '', ageGroup: 'U12', date: '', fields: ['Field 1','Field 2'], slotMinutes: '60' });
  const [gameForm, setGameForm] = useState({ homeTeamId: '', awayTeamId: '', date: '', time: '08:00', field: 'Field 1', ageGroup: 'U12', round: 'Pool A', tournamentId: '' });
  const [scoreForm, setScoreForm] = useState({ homeScore: '', awayScore: '' });
  const [genPreview, setGenPreview] = useState(null);

  const getTeam = id => teams.find(t => t.id === id);

  const allDates = [...new Set(games.map(g => g.date))].sort();
  const allAGs = [...new Set(games.map(g => g.ageGroup))].sort();

  const filtered = games.filter(g => {
    if (filterTrn !== 'all' && g.tournamentId !== filterTrn) return false;
    if (filterDate !== 'all' && g.date !== filterDate) return false;
    if (filterAG !== 'all' && g.ageGroup !== filterAG) return false;
    return true;
  });

  const byDate = filtered.reduce((acc, g) => {
    const d = g.date || 'TBD';
    if (!acc[d]) acc[d] = [];
    acc[d].push(g);
    return acc;
  }, {});

  const previewSchedule = () => {
    const trn = tournaments.find(t => t.id === genForm.tournamentId);
    const ag = trn?.ageGroups?.find(a => a.group === genForm.ageGroup);
    const teamIds = ag?.teams || [];
    const teamObjs = teamIds.map(id => getTeam(id)).filter(Boolean);
    if (teamObjs.length < 2) return alert('Not enough teams registered for that age group.');
    const preview = generateSchedule(teamObjs, genForm.date || '2025-06-07', genForm.fields, Number(genForm.slotMinutes));
    setGenPreview(preview);
  };

  const confirmGenerate = () => {
    genPreview.forEach(g => addGame({ ...g, tournamentId: genForm.tournamentId }));
    setShowGenerate(false);
    setGenPreview(null);
  };

  const submitGame = (e) => {
    e.preventDefault();
    addGame(gameForm);
    setShowAdd(false);
    setGameForm({ homeTeamId: '', awayTeamId: '', date: '', time: '08:00', field: 'Field 1', ageGroup: 'U12', round: 'Pool A', tournamentId: '' });
  };

  const saveScore = () => {
    updateGame(editScore.id, { homeScore: Number(scoreForm.homeScore), awayScore: Number(scoreForm.awayScore) });
    setEditScore(null);
  };

  return (
    <div>
      <PageHeader title="Schedule" subtitle="View and manage game schedules across all events"
        actions={isAdmin && <>
          <Button variant="secondary" size="sm"><Printer size={14} /> Print</Button>
          <Button variant="secondary" onClick={() => setShowGenerate(true)}><Zap size={14} /> Auto-generate</Button>
          <Button variant="primary" onClick={() => setShowAdd(true)}><Plus size={15} /> Add game</Button>
        </>}
      />

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: '1.25rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <Filter size={15} color="var(--text-muted)" />
        <Select style={{ width: 'auto' }} value={filterTrn} onChange={e => setFilterTrn(e.target.value)}>
          <option value="all">All tournaments</option>
          {tournaments.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </Select>
        <Select style={{ width: 'auto' }} value={filterDate} onChange={e => setFilterDate(e.target.value)}>
          <option value="all">All dates</option>
          {allDates.map(d => <option key={d} value={d}>{d}</option>)}
        </Select>
        <Select style={{ width: 'auto' }} value={filterAG} onChange={e => setFilterAG(e.target.value)}>
          <option value="all">All age groups</option>
          {allAGs.map(ag => <option key={ag}>{ag}</option>)}
        </Select>
        <span style={{ fontSize: 13, color: 'var(--text-muted)', marginLeft: 'auto' }}>{filtered.length} games</span>
      </div>

      {/* Games by date */}
      {Object.keys(byDate).length === 0 ? (
        <Card><p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '2rem' }}>No games match your filters. Try auto-generating a schedule.</p></Card>
      ) : Object.entries(byDate).sort().map(([date, dayGames]) => (
        <div key={date} style={{ marginBottom: '1.25rem' }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 4, height: 18, background: 'var(--gold-main)', borderRadius: 2 }} />
            {date === 'TBD' ? 'Date TBD' : formatDate(date)}
            <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 500 }}>{dayGames.length} games</span>
          </h3>
          <Card padding="0">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: 'var(--surface-1)' }}>
                  {['Time','Home team','','Away team','Field','Age','Round','Score',''].map((h,i) => (
                    <th key={i} style={{ padding: '8px 12px', textAlign: h === '' ? 'center' : 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid var(--border)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dayGames.sort((a,b) => a.time?.localeCompare(b.time)).map(g => {
                  const home = getTeam(g.homeTeamId);
                  const away = getTeam(g.awayTeamId);
                  const hasScore = g.homeScore !== null;
                  return (
                    <tr key={g.id} style={{ borderBottom: '1px solid var(--border)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-1)'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}>
                      <td style={{ padding: '10px 12px', fontWeight: 700, color: 'var(--gold-main)', whiteSpace: 'nowrap' }}>{g.time}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                          <div style={{ width: 8, height: 8, borderRadius: '50%', background: home?.color || '#94a3b8' }} />
                          <span style={{ fontWeight: hasScore && g.homeScore > g.awayScore ? 700 : 500 }}>{home?.name || 'TBD'}</span>
                        </div>
                      </td>
                      <td style={{ padding: '10px 8px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 12, fontWeight: 600 }}>vs</td>
                      <td style={{ padding: '10px 12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                          <div style={{ width: 8, height: 8, borderRadius: '50%', background: away?.color || '#94a3b8' }} />
                          <span style={{ fontWeight: hasScore && g.awayScore > g.homeScore ? 700 : 500 }}>{away?.name || 'TBD'}</span>
                        </div>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ fontSize: 12, background: 'var(--surface-2)', color: 'var(--text-secondary)', padding: '2px 8px', borderRadius: 6, fontWeight: 600 }}>{g.field}</span>
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)' }}>{g.ageGroup}</td>
                      <td style={{ padding: '10px 12px', fontSize: 12, color: 'var(--text-muted)' }}>{g.round}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        {hasScore ? (
                          <span style={{ fontWeight: 800, fontFamily: 'var(--font-condensed)', fontSize: 16 }}>
                            <span style={{ color: g.homeScore > g.awayScore ? 'var(--gold-deep)' : 'var(--text-muted)' }}>{g.homeScore}</span>
                            <span style={{ color: 'var(--text-muted)', margin: '0 4px' }}>–</span>
                            <span style={{ color: g.awayScore > g.homeScore ? 'var(--gold-deep)' : 'var(--text-muted)' }}>{g.awayScore}</span>
                          </span>
                        ) : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>–</span>}
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        {isAdmin && (
                          <button onClick={() => { setEditScore(g); setScoreForm({ homeScore: g.homeScore ?? '', awayScore: g.awayScore ?? '' }); }}
                            style={{ fontSize: 12, color: 'var(--gold-main)', background: 'none', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                            {hasScore ? 'Edit score' : 'Enter score'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        </div>
      ))}

      {/* Add Game Modal */}
      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Add game">
        <form onSubmit={submitGame} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <FormField label="Home team" required>
              <Select value={gameForm.homeTeamId} onChange={e => setGameForm(p => ({...p, homeTeamId: e.target.value}))}>
                <option value="">Select…</option>
                {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </Select>
            </FormField>
            <FormField label="Away team" required>
              <Select value={gameForm.awayTeamId} onChange={e => setGameForm(p => ({...p, awayTeamId: e.target.value}))}>
                <option value="">Select…</option>
                {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </Select>
            </FormField>
            <FormField label="Date"><Input type="date" value={gameForm.date} onChange={e => setGameForm(p => ({...p, date: e.target.value}))} /></FormField>
            <FormField label="Time">
              <Select value={gameForm.time} onChange={e => setGameForm(p => ({...p, time: e.target.value}))}>
                {TIME_SLOTS.map(t => <option key={t}>{t}</option>)}
              </Select>
            </FormField>
            <FormField label="Field">
              <Select value={gameForm.field} onChange={e => setGameForm(p => ({...p, field: e.target.value}))}>
                {FIELDS.map(f => <option key={f}>{f}</option>)}
              </Select>
            </FormField>
            <FormField label="Age group">
              <Select value={gameForm.ageGroup} onChange={e => setGameForm(p => ({...p, ageGroup: e.target.value}))}>
                {['U8','U10','U12','U14','U16','U19'].map(g => <option key={g}>{g}</option>)}
              </Select>
            </FormField>
            <FormField label="Round">
              <Select value={gameForm.round} onChange={e => setGameForm(p => ({...p, round: e.target.value}))}>
                {ROUNDS.map(r => <option key={r}>{r}</option>)}
              </Select>
            </FormField>
            <FormField label="Tournament">
              <Select value={gameForm.tournamentId} onChange={e => setGameForm(p => ({...p, tournamentId: e.target.value}))}>
                <option value="">None</option>
                {tournaments.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </Select>
            </FormField>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button type="button" variant="secondary" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button type="submit" variant="primary">Add game</Button>
          </div>
        </form>
      </Modal>

      {/* Auto-generate Modal */}
      <Modal open={showGenerate} onClose={() => { setShowGenerate(false); setGenPreview(null); }} title="Auto-generate schedule" width={560}>
        {!genPreview ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ background: 'var(--gold-pale)', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: 'var(--gold-deep)' }}>
              Automatically creates round-robin pool games for all registered teams in a given age group.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <FormField label="Tournament">
                <Select value={genForm.tournamentId} onChange={e => setGenForm(p => ({...p, tournamentId: e.target.value}))}>
                  {tournaments.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </Select>
              </FormField>
              <FormField label="Age group">
                <Select value={genForm.ageGroup} onChange={e => setGenForm(p => ({...p, ageGroup: e.target.value}))}>
                  {['U8','U10','U12','U14','U16','U19'].map(g => <option key={g}>{g}</option>)}
                </Select>
              </FormField>
              <FormField label="Date"><Input type="date" value={genForm.date} onChange={e => setGenForm(p => ({...p, date: e.target.value}))} /></FormField>
              <FormField label="Slot length (min)">
                <Select value={genForm.slotMinutes} onChange={e => setGenForm(p => ({...p, slotMinutes: e.target.value}))}>
                  {['45','60','75','90'].map(m => <option key={m}>{m}</option>)}
                </Select>
              </FormField>
            </div>
            <FormField label="Available fields">
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {FIELDS.map(f => (
                  <label key={f} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 14 }}>
                    <input type="checkbox" checked={genForm.fields.includes(f)} onChange={e => {
                      setGenForm(p => ({ ...p, fields: e.target.checked ? [...p.fields, f] : p.fields.filter(x => x !== f) }));
                    }} />
                    {f}
                  </label>
                ))}
              </div>
            </FormField>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={() => setShowGenerate(false)}>Cancel</Button>
              <Button variant="primary" onClick={previewSchedule}><Zap size={13} /> Preview schedule</Button>
            </div>
          </div>
        ) : (
          <div>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>Preview: {genPreview.length} games generated</p>
            <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 10, marginBottom: 14 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr style={{ background: 'var(--surface-1)' }}>
                  {['Time','Home','Away','Field'].map(h => <th key={h} style={{ padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid var(--border)', fontWeight: 600, color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {genPreview.map((g, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '8px 10px', fontWeight: 700, color: 'var(--gold-main)' }}>{g.time}</td>
                      <td style={{ padding: '8px 10px' }}>{getTeam(g.homeTeamId)?.name}</td>
                      <td style={{ padding: '8px 10px' }}>{getTeam(g.awayTeamId)?.name}</td>
                      <td style={{ padding: '8px 10px', color: 'var(--text-muted)' }}>{g.field}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={() => setGenPreview(null)}>← Edit settings</Button>
              <Button variant="primary" onClick={confirmGenerate}>Confirm & save {genPreview.length} games</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Score modal */}
      <Modal open={!!editScore} onClose={() => setEditScore(null)} title="Enter score" width={360}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 12, alignItems: 'center' }}>
            <FormField label={getTeam(editScore?.homeTeamId)?.name || 'Home'}>
              <Input type="number" min="0" value={scoreForm.homeScore} onChange={e => setScoreForm(p => ({...p, homeScore: e.target.value}))} style={{ textAlign: 'center', fontSize: 22, fontWeight: 800 }} />
            </FormField>
            <span style={{ paddingTop: 20, color: 'var(--text-muted)', fontWeight: 700 }}>–</span>
            <FormField label={getTeam(editScore?.awayTeamId)?.name || 'Away'}>
              <Input type="number" min="0" value={scoreForm.awayScore} onChange={e => setScoreForm(p => ({...p, awayScore: e.target.value}))} style={{ textAlign: 'center', fontSize: 22, fontWeight: 800 }} />
            </FormField>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setEditScore(null)}>Cancel</Button>
            <Button variant="primary" onClick={saveScore}>Save score</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
